import { useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useConnectionNames } from '@/entities/connection';
import { groupByConnection, useDatasets } from '@/features/catalog-browser';
import type { CatalogRow } from '@/shared/api/generated/catalog';

import { useReachLookup } from '../api/lineage';
import { summaryFor, summaryKey } from '../model/presentation';

/* The dataset chooser, and the reason it is a bar rather than the page.
 *
 * **`LineagePicker` is a whole page and stops being reachable the moment a dataset is chosen.** That
 * is right for arriving at `/lineage` with no idea which table to look at — it lists every dataset
 * under its source with the reach beside it, which a dropdown cannot do. It is wrong for the next
 * thing a reader does, which is look at a second table: the only way back was a "← All datasets"
 * link that threw away the graph, and comparing two datasets meant two round trips through a
 * full-page list.
 *
 * So the bar stays on screen with the graph under it, and switching dataset is one interaction. The
 * page-sized picker still renders *below* it while nothing is selected, so the browse-by-source view
 * and its four reach states are not lost — the bar is an addition, not a replacement.
 *
 * **A combobox rather than a `<select>`, and the search is the whole reason.** `LineagePicker`'s own
 * note records why a `<select>` was removed: it answers "which of these do I want" only for somebody
 * who already knows the name. A filtered listbox keeps that answer available *and* works for the
 * reader who does know — which is exactly the reader who has a bar rather than a page.
 *
 * **Every option carries its reach**, from the same `useReachLookup` the picker uses, so the bar
 * cannot disagree with the list below it about whether a dataset has lineage. A reader must not pick
 * a dataset from this bar and land on an empty canvas without having been told.
 *
 * ARIA's combobox pattern, spelled out rather than approximated: `role="combobox"` on the input with
 * `aria-expanded` and `aria-controls`, `role="listbox"`/`role="option"` below, `aria-activedescendant`
 * naming the highlighted option, and arrow keys, Enter and Escape all bound. An SVG graph a reader
 * cannot reach by keyboard is a graph an auditor cannot audit, and the same is true of its selector.
 */

/** How many matches the listbox will show at once.
 *
 * A bound rather than a scroll of five thousand: an estate this platform is sold into has tens of
 * thousands of tables, and rendering every match into the DOM on each keystroke is the shape that
 * makes a filter feel broken. The count of matches is reported above the list, so a reader can see
 * that narrowing further is worthwhile rather than concluding the rest do not exist.
 */
const VISIBLE_OPTIONS = 40;

export interface DatasetBarProps {
  /** The dataset currently on screen, so the bar shows what the graph is about. */
  readonly selectedDatasetId: string | null;
  readonly onPick: (datasetId: string) => void;
}

export function DatasetBar({ selectedDatasetId, onPick }: DatasetBarProps): ReactElement {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const datasets = useDatasets({ search: '', connectionId: null });
  const connectionNames = useConnectionNames();
  const lookup = useReachLookup();

  const rows = useMemo(() => datasets.data?.items ?? [], [datasets.data]);

  /* Grouped by connection through `catalog-browser`'s own helper, so the headings in this list mean
     what they mean everywhere else — including the four labels a missing connection name needs. */
  const groups = useMemo(
    () =>
      groupByConnection(rows, {
        status: connectionNames.isPending
          ? 'loading'
          : connectionNames.isError
            ? 'unreadable'
            : 'ready',
        names: connectionNames.data ?? new Map<string, string>(),
      }),
    [rows, connectionNames.isPending, connectionNames.isError, connectionNames.data],
  );

  /* One flat list of matches in the grouped order, because keyboard navigation is linear and the
     headings are decoration on top of it. Built from the groups rather than from `rows` so the
     order a reader sees and the order the arrow keys walk cannot diverge. */
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const found: { readonly row: CatalogRow; readonly connection: string }[] = [];
    for (const group of groups) {
      const heading =
        group.label.kind === 'named'
          ? group.label.name
          : t(`catalog.group.${group.label.kind}`);
      for (const row of group.rows) {
        const name = `${row.schema_name}.${row.object_name}`;
        /* The connection name is searchable as well as the dataset's, because "which of my two
           Snowflakes was that in" is the question a bar spanning several connections invites — and
           it is the question the connection context on every node exists to answer. */
        if (
          needle === '' ||
          name.toLowerCase().includes(needle) ||
          heading.toLowerCase().includes(needle)
        ) {
          found.push({ row, connection: heading });
        }
      }
    }
    return found;
  }, [groups, query, t]);

  const shown = matches.slice(0, VISIBLE_OPTIONS);
  const selectedRow = rows.find((row) => row.id === selectedDatasetId) ?? null;
  const selectedName =
    selectedRow === null ? '' : `${selectedRow.schema_name}.${selectedRow.object_name}`;

  const choose = (datasetId: string): void => {
    onPick(datasetId);
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  return (
    <div className="lq-dsbar">
      <label className="lq-dsbar__label" htmlFor="lineage-dataset-bar">
        {t('lineage.bar.label')}
      </label>
      <div className="lq-dsbar__field">
        <input
          id="lineage-dataset-bar"
          ref={inputRef}
          className="lq-dsbar__input"
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="lineage-dataset-listbox"
          aria-autocomplete="list"
          {...(open && shown.length > 0
            ? { 'aria-activedescendant': `lineage-dataset-option-${String(highlighted)}` }
            : {})}
          /* The chosen dataset's name is the placeholder rather than the value, so opening the bar
             does not require clearing what is already there — a reader switching datasets types
             immediately. An empty value with a filled placeholder is also what tells them the field
             is a search rather than the current selection. */
          placeholder={selectedName === '' ? t('lineage.bar.placeholder') : selectedName}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setHighlighted(0);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          /* A blur closes the list, deferred so a click on an option lands first — without the
             delay the option unmounts under the pointer and the click reaches nothing, which is the
             classic combobox defect. */
          onBlur={() => {
            window.setTimeout(() => {
              setOpen(false);
            }, 120);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
              return;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              setOpen(true);
              setHighlighted((current) => {
                if (shown.length === 0) return 0;
                const next = event.key === 'ArrowDown' ? current + 1 : current - 1;
                /* Clamped rather than wrapped. A wrap turns "I am at the end of the list" into a
                   silent jump back to the top, and on a filtered list a reader cannot tell the two
                   apart. */
                return Math.min(Math.max(next, 0), shown.length - 1);
              });
              return;
            }
            if (event.key === 'Enter') {
              event.preventDefault();
              const picked = shown[highlighted];
              if (picked !== undefined) choose(picked.row.id);
            }
          }}
        />
        {selectedName !== '' && (
          <span className="lq-dsbar__current" aria-live="polite">
            {selectedName}
          </span>
        )}
      </div>

      {open && (
        <ul className="lq-dsbar__list" id="lineage-dataset-listbox" role="listbox">
          {datasets.isPending ? (
            <li className="lq-dsbar__note">{t('lineage.bar.loading')}</li>
          ) : datasets.isError ? (
            /* A failed catalogue read is about the request, not about the estate. Saying "no
               datasets match" here would report our own failure as a fact about their data. */
            <li className="lq-dsbar__note">{t('lineage.bar.unreadable')}</li>
          ) : shown.length === 0 ? (
            <li className="lq-dsbar__note">{t('lineage.bar.noMatch')}</li>
          ) : (
            <>
              {shown.map((match, index) => {
                const name = `${match.row.schema_name}.${match.row.object_name}`;
                const summary = summaryFor(match.row.id, lookup);
                return (
                  <li
                    key={match.row.id}
                    id={`lineage-dataset-option-${String(index)}`}
                    role="option"
                    aria-selected={match.row.id === selectedDatasetId}
                    className={
                      index === highlighted
                        ? 'lq-dsbar__option lq-dsbar__option--on'
                        : 'lq-dsbar__option'
                    }
                    onMouseEnter={() => {
                      setHighlighted(index);
                    }}
                    onMouseDown={(event) => {
                      /* `onMouseDown` rather than `onClick`: the input's blur fires first and the
                         deferred close would otherwise race the click. */
                      event.preventDefault();
                      choose(match.row.id);
                    }}
                  >
                    <span className="lq-dsbar__name">{name}</span>
                    <span className="lq-dsbar__where">{match.connection}</span>
                    {/* The reach, from the same lookup the list below uses. A reader must not pick
                        a dataset here and land on an empty canvas unwarned. */}
                    <span className="lq-dsbar__reach">
                      {summary.kind === 'counted'
                        ? t('lineage.pick.counted', {
                            upstream: summary.upstream,
                            downstream: summary.downstream,
                          })
                        : t(summaryKey(summary))}
                    </span>
                  </li>
                );
              })}
              {matches.length > shown.length && (
                /* Truncation reported with the exact figure, never hidden: a filter that silently
                   shows a subset is worse than no filter. */
                <li className="lq-dsbar__note">
                  {t('lineage.bar.truncated', {
                    shown: shown.length,
                    total: matches.length,
                  })}
                </li>
              )}
            </>
          )}
        </ul>
      )}
    </div>
  );
}
